import { createContext, useContext, type ReactNode } from 'react'
import { getPages } from './api/client'
import type { PageLink } from './api/types'
import { useResource } from './hooks/useResource'

/**
 * Die Seiten des Ladens, einmal geholt.
 *
 * Wie die Grunddaten eine Ausnahme von „jede Seite holt ihre Daten selbst":
 * Der Fußbereich steht unter jeder Seite, und die Kasse muss wissen, ob es
 * eine Widerrufsbelehrung gibt, auf die sie vor dem Bestellknopf verweisen
 * kann. Zwei Aufrufe für dieselbe Liste wären zwei Gelegenheiten, verschiedene
 * Antworten zu bekommen.
 *
 * Nicht in den Grunddaten, weil die Liste dort nicht steht — das Aussehen
 * schon, und deshalb wird es auch nicht nachgeladen.
 */
const ShopPagesContext = createContext<PageLink[]>([])

export function ShopPagesProvider({ children }: { children: ReactNode }) {
  const { data } = useResource((signal) => getPages(signal), [])

  // Kein Fehlerzustand nach außen: Wenn die Liste nicht kommt, fehlen Verweise
  // im Fuß — der Laden verkauft trotzdem weiter. Eine Fehlermeldung unter
  // jeder Seite wäre die schlechtere Antwort auf dasselbe Problem.
  return <ShopPagesContext.Provider value={data ?? []}>{children}</ShopPagesContext.Provider>
}

/** Alle Seiten, die in den Fußbereich gehören. */
export function useShopPages(): PageLink[] {
  return useContext(ShopPagesContext)
}

/** Eine bestimmte Seite, falls es sie gibt — etwa die Widerrufsbelehrung. */
export function useShopPage(handle: string): PageLink | undefined {
  return useShopPages().find((seite) => seite.handle === handle)
}
